// R-104 chunk-cycle fix: NO top-level `import { useStore } from '../../store'`
// and NO top-level `import { syncBrowserUrlForActiveSite }`. Those static
// imports made the reportGuide chunk a *pure leaf* of the entry chunk (its only
// first-party static dep WAS the entry chunk), so Vite emitted the guide's
// dynamic import() with an EMPTY `__vite__mapDeps([])` and the module namespace
// resolved to `undefined` at import time → "Cannot read properties of undefined
// (reading 'ReportQualityGuidePage')" on /guide/reports. The store + URL helper
// are now pulled in lazily, at CALL TIME, inside applyGuideDeepLink (which is
// therefore async). See vite.config.ts manualChunks + the regression test.

import type { SiteTabId } from '../siterag/siteTabIds';

export const GUIDE_PENDING_KEY = 'reportGuidePendingAction';

// THE real `Tab` union on SiteLandingPage (and the `tab=` URL param), via the
// shared siteTabIds leaf. Equipment (BOM) is `supplychain`, not `equipment`.
// Type-only import from a zero-dependency const module — this does NOT add a
// static first-party dep, so the R-104 constraint above still holds.
export type GuideSiteTab = SiteTabId;

export type GuideDeepLinkTarget =
  | { kind: 'new-site' }
  | {
      kind: 'site-tab';
      siteId: string;
      tab: GuideSiteTab;
    }
  | {
      kind: 'boundary-draw';
      siteId: string;
    }
  | {
      kind: 'boundary-import';
      siteId: string;
    }
  | {
      kind: 'layers';
      siteId: string;
    }
  | {
      kind: 'new-report';
      siteId: string;
    }
  // Cross-site portfolio is a top-level route, not a site tab.
  | { kind: 'portfolio' };

/** SiteLandingPage consumes these after navigation. */
export interface GuideSiteLandingFollowUp {
  openBoundary?: 'location' | 'boundary' | 'boundary-import';
  openLayerEditor?: boolean;
  openReportWizard?: boolean;
}

export interface GuidePendingAction {
  target: GuideDeepLinkTarget;
  siteLanding?: GuideSiteLandingFollowUp;
}

export function queueGuidePendingAction(action: GuidePendingAction): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(GUIDE_PENDING_KEY, JSON.stringify(action));
  } catch { /* quota */ }
}

export function readGuidePendingAction(): GuidePendingAction | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(GUIDE_PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as GuidePendingAction;
  } catch {
    return null;
  }
}

export function clearGuidePendingAction(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(GUIDE_PENDING_KEY);
  } catch { /* ignore */ }
}

export function consumeGuidePendingActionForSite(siteId: string): GuideSiteLandingFollowUp | null {
  const pending = readGuidePendingAction();
  if (!pending?.siteLanding) return null;
  const t = pending.target;
  if (t.kind === 'new-site') return null;
  if ('siteId' in t && t.siteId !== siteId) return null;
  clearGuidePendingAction();
  return pending.siteLanding;
}

export type GuideDeepLinkResult = 'ok' | 'need-site';

export async function applyGuideDeepLink(
  target: GuideDeepLinkTarget,
  options?: {
    openNewSiteWizard?: () => void;
    /**
     * When the caller navigates itself via react-router (the guide page does),
     * skip this module's own history.pushState so a single deep-link doesn't
     * create two history entries (Back previously needed two presses).
     */
    skipUrlUpdate?: boolean;
  },
): Promise<GuideDeepLinkResult> {
  // Lazy, call-time imports (see header note): keep the store + URL helper OUT
  // of this module's static closure so the reportGuide chunk is not a pure leaf
  // of the entry chunk.
  const { useStore } = await import('../../store');
  const { syncBrowserUrlForActiveSite } = await import('../siterag/useUrlSiteHydration');

  const store = useStore.getState();
  store.setViewMode('siterag');

  if (target.kind === 'new-site') {
    clearGuidePendingAction();
    queueGuidePendingAction({ target: { kind: 'new-site' } });
    if (typeof window !== 'undefined' && !options?.skipUrlUpdate) {
      window.history.pushState({}, '', '/');
    }
    options?.openNewSiteWizard?.();
    return 'ok';
  }

  if (target.kind === 'portfolio') {
    // Cross-site portfolio lives at /customers/:customerId/portfolio and is
    // scoped to the user's company, not the active site. Needs a company.
    const customerId = store.companyId;
    if (!customerId) return 'need-site';
    clearGuidePendingAction();
    if (typeof window !== 'undefined' && !options?.skipUrlUpdate) {
      window.history.pushState({}, '', `/customers/${customerId}/portfolio`);
    }
    return 'ok';
  }

  const siteId = target.siteId;
  const site = store.siteRag.sites.find(s => s.id === siteId);
  if (!site) return 'need-site';

  store.setActiveSite(siteId);
  syncBrowserUrlForActiveSite(siteId);

  // `home` is the no-query default tab on SiteLandingPage; the boundary/layer
  // editors are owned by the Site Map tab, so those follow-ups land on `map`.
  let tab: GuideSiteTab = 'home';
  let siteLanding: GuideSiteLandingFollowUp | undefined;

  switch (target.kind) {
    case 'site-tab':
      tab = target.tab;
      break;
    case 'boundary-draw':
      tab = 'map';
      siteLanding = { openBoundary: 'boundary' };
      break;
    case 'boundary-import':
      tab = 'map';
      siteLanding = { openBoundary: 'boundary-import' };
      break;
    case 'layers': {
      const { isSiteLayerSetWriteReady } = await import('../../lib/siteLayers/canonicalLayerResolution');
      tab = 'map';
      if (isSiteLayerSetWriteReady(site)) {
        siteLanding = { openLayerEditor: true };
      } else {
        const { toast } = await import('sonner');
        toast.error(
          site._siteLayersHydrationError
            ? 'Site layers failed to load completely. Refresh the site before editing layers.'
            : 'Site layers are still loading. Wait for layer hydration to finish before editing layers.',
        );
      }
      break;
    }
    case 'new-report':
      tab = 'reports';
      siteLanding = { openReportWizard: true };
      break;
    default:
      break;
  }

  if (typeof window !== 'undefined' && !options?.skipUrlUpdate) {
    const url = new URL(window.location.href);
    url.pathname = `/sites/${siteId}`;
    url.search = tab === 'home' ? '' : `tab=${tab}`;
    window.history.pushState({}, '', url.toString());
  }

  queueGuidePendingAction({ target, siteLanding });
  return 'ok';
}

export function buildGuideDeepLinkFromStep(
  kind: import('./reportGuideSteps').GuideDeepLinkKind,
  siteId: string | null,
  tab?: GuideSiteTab,
): GuideDeepLinkTarget | null {
  if (kind === 'new-site') return { kind: 'new-site' };
  if (kind === 'portfolio') return { kind: 'portfolio' };
  if (!siteId) return null;
  switch (kind) {
    case 'site-tab':
      return { kind: 'site-tab', siteId, tab: tab ?? 'home' };
    case 'boundary-draw':
      return { kind: 'boundary-draw', siteId };
    case 'boundary-import':
      return { kind: 'boundary-import', siteId };
    case 'new-report':
      return { kind: 'new-report', siteId };
    default:
      return null;
  }
}
